import { useState, useEffect } from 'react';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';
import { Button } from '../../components/ui';

const ACCENT = '#0085CA';
const NAVY   = '#003D6B';
const LIGHT  = '#EEF6FC';

function MIcon({ name, size = 18, style = {} }) {
  return <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, verticalAlign: 'middle', ...style }}>{name}</span>;
}

const inputSt  = { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' };
const selectSt = { ...inputSt, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', paddingRight: 28 };

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 5 }}>{children}</div>;
}

function Select({ value, onChange, children, style = {} }) {
  return (
    <div style={{ position: 'relative', display: 'inline-block', ...style }}>
      <select value={value} onChange={onChange} style={{ ...selectSt, width: '100%' }}>{children}</select>
      <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9CA3AF', fontSize: 10 }}>▾</span>
    </div>
  );
}

function Chip({ active, onClick, color, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 16, border: '1px solid ' + (active ? (color || ACCENT) : '#E5E7EB'),
      background: active ? (color ? color + '22' : LIGHT) : '#fff',
      color: active ? (color || ACCENT) : '#6B7280',
      fontSize: 12, fontWeight: active ? 700 : 400, cursor: 'pointer', whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function StatCard({ icon, label, value, color = ACCENT }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', flex: 1, minWidth: 150, borderTop: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <MIcon name={icon} size={15} style={{ color }} />
        <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: NAVY }}>{value ?? '—'}</div>
    </div>
  );
}

export default function ReportsPage() {
  const [rooms,      setRooms]      = useState([]);
  const [categories, setCategories] = useState([]);
  const [tariffs,    setTariffs]    = useState([]);
  const [saved,      setSaved]      = useState([]);
  const [report,     setReport]     = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [tab,        setTab]        = useState('report');
  const [saveModal,  setSaveModal]  = useState(false);
  const [saveName,   setSaveName]   = useState('');
  const [showRooms,  setShowRooms]  = useState(false);

  const [filters, setFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    date_to:   new Date().toISOString().slice(0, 10),
    category_ids: [], room_ids: [], status: 'confirmed',
    requester_email: '', calc_mode: 'hours', include_pricing: false, group_by: 'category',
  });
  const ff = v => setFilters(p => ({ ...p, ...v }));

  function toggleArr(key, val) {
    ff({ [key]: filters[key].includes(val) ? filters[key].filter(x => x !== val) : [...filters[key], val] });
  }

  function setQuickPeriod(type) {
    const d = new Date();
    if (type === 'month')  ff({ date_from: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0,10),   date_to: d.toISOString().slice(0,10) });
    if (type === 'prev')   { const f = new Date(d.getFullYear(), d.getMonth()-1, 1); const t = new Date(d.getFullYear(), d.getMonth(), 0); ff({ date_from: f.toISOString().slice(0,10), date_to: t.toISOString().slice(0,10) }); }
    if (type === 'year')   ff({ date_from: `${d.getFullYear()}-01-01`, date_to: d.toISOString().slice(0,10) });
    if (type === 'week')   { const f = new Date(d); f.setDate(d.getDate()-7); ff({ date_from: f.toISOString().slice(0,10), date_to: d.toISOString().slice(0,10) }); }
  }

  useEffect(() => {
    document.title = 'Informes — UNEATLANTICO';
    const tok = { headers: { Authorization: `Bearer ${localStorage.getItem('hd_token')}` } };
    Promise.all([
      api.getRooms(), api.getRoomCategories(),
      fetch('/api/reports/tariffs', tok).then(r => r.json()),
      fetch('/api/reports/saved',   tok).then(r => r.json()),
    ]).then(([r, c, t, s]) => { setRooms(r); setCategories(c); setTariffs(Array.isArray(t)?t:[]); setSaved(Array.isArray(s)?s:[]); });
    return () => { document.title = 'HelpDesk TI — UNEATLANTICO'; };
  }, []);

  async function runReport() {
    setLoading(true);
    try {
      const res = await fetch('/api/reports/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hd_token')}` },
        body: JSON.stringify(filters),
      });
      setReport(await res.json());
    } finally { setLoading(false); }
  }

  async function exportCSV() {
    const res = await fetch('/api/reports/export/csv', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hd_token')}` },
      body: JSON.stringify(filters),
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: `informe-${filters.date_from}-${filters.date_to}.csv` }).click();
    URL.revokeObjectURL(url);
  }

  async function doSave() {
    if (!saveName.trim()) return;
    const res = await fetch('/api/reports/saved', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hd_token')}` },
      body: JSON.stringify({ name: saveName, filters }),
    });
    const saved2 = await res.json();
    setSaved(p => [saved2, ...p]);
    setSaveModal(false); setSaveName('');
  }

  async function deleteSaved(id) {
    await fetch(`/api/reports/saved/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('hd_token')}` } });
    setSaved(p => p.filter(s => s.id !== id));
  }

  const filteredRooms = filters.category_ids.length ? rooms.filter(r => filters.category_ids.includes(r.category_id)) : rooms;
  const selectedCatNames = categories.filter(c => filters.category_ids.includes(c.id)).map(c => c.name);
  const selectedRoomNames = rooms.filter(r => filters.room_ids.includes(r.id)).map(r => r.name);

  return (
    <BackDeskLayout title="Informes y Facturación" actions={
      <div style={{ display: 'flex', gap: 6 }}>
        {[['report','bar_chart','Informe'],['tariffs','payments','Tarifas'],['saved','bookmark',`Guardados (${saved.length})`]].map(([t, icon, label]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '5px 13px', borderRadius: 7, border: '1px solid ' + (tab===t ? ACCENT : '#E5E7EB'),
            background: tab===t ? LIGHT : '#fff', color: tab===t ? ACCENT : '#6B7280',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <MIcon name={icon} size={13}/>{label}
          </button>
        ))}
      </div>
    }>
    <>
      {/* ── TAB: INFORME ── */}
      {tab === 'report' && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', width: '100%' }}>

          {/* ── Columna izquierda: filtros ── */}
          <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', width: 260, flexShrink: 0, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MIcon name="filter_list" size={15} style={{ color: ACCENT }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Filtros</span>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Período */}
              <div>
                <Label>Período</Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="date" value={filters.date_from} onChange={e => ff({ date_from: e.target.value })} style={{ ...inputSt, flex: 1 }} />
                    <span style={{ color: '#D1D5DB', fontSize: 11 }}>—</span>
                    <input type="date" value={filters.date_to}   onChange={e => ff({ date_to: e.target.value })} style={{ ...inputSt, flex: 1 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {[['week','Semana'],['month','Mes'],['prev','Mes ant.'],['year','Año']].map(([k,l]) => (
                      <button key={k} onClick={() => setQuickPeriod(k)}
                        style={{ padding: '3px 9px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#F9FAFB', fontSize: 11, cursor: 'pointer', color: '#6B7280' }}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Categorías */}
              <div>
                <Label>Categorías</Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {categories.map(cat => (
                    <label key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 8px', borderRadius: 6, background: filters.category_ids.includes(cat.id) ? LIGHT : 'transparent', border: '1px solid ' + (filters.category_ids.includes(cat.id) ? ACCENT+'44' : 'transparent') }}>
                      <input type="checkbox" checked={filters.category_ids.includes(cat.id)} onChange={() => toggleArr('category_ids', cat.id)} style={{ accentColor: ACCENT }} />
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: '#374151' }}>{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Salas */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Label>Salas específicas</Label>
                  {filters.room_ids.length > 0 && (
                    <button onClick={() => ff({ room_ids: [] })} style={{ fontSize: 10, color: '#B91C1C', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Limpiar</button>
                  )}
                </div>
                <div style={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3, paddingRight: 2 }}>
                  {filteredRooms.map(r => (
                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, color: '#374151', padding: '2px 4px', borderRadius: 4, background: filters.room_ids.includes(r.id) ? LIGHT : 'transparent' }}>
                      <input type="checkbox" checked={filters.room_ids.includes(r.id)} onChange={() => toggleArr('room_ids', r.id)} style={{ accentColor: ACCENT }} />
                      {r.name}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Estado */}
                <div>
                  <Label>Estado</Label>
                  <Select value={filters.status} onChange={e => ff({ status: e.target.value })} style={{ width: '100%' }}>
                    <option value="all">Todas</option>
                    <option value="confirmed">Confirmadas</option>
                    <option value="pending">Pendientes</option>
                  </Select>
                </div>
                {/* Agrupar por */}
                <div>
                  <Label>Agrupar por</Label>
                  <Select value={filters.group_by} onChange={e => ff({ group_by: e.target.value })} style={{ width: '100%' }}>
                    <option value="category">Categoría</option>
                    <option value="room">Sala</option>
                    <option value="requester">Solicitante</option>
                    <option value="none">Sin agrupar</option>
                  </Select>
                </div>
                {/* Duración */}
                <div>
                  <Label>Calcular duración por</Label>
                  <div style={{ display: 'flex', borderRadius: 7, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    {[['hours','Horas'],['days','Días']].map(([v,l]) => (
                      <button key={v} onClick={() => ff({ calc_mode: v })} style={{
                        flex: 1, padding: '7px', border: 'none',
                        background: filters.calc_mode===v ? ACCENT : '#fff',
                        color: filters.calc_mode===v ? '#fff' : '#6B7280',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}>{l}</button>
                    ))}
                  </div>
                </div>
                {/* Email */}
                <div>
                  <Label>Solicitante (email)</Label>
                  <input value={filters.requester_email} onChange={e => ff({ requester_email: e.target.value })}
                    placeholder="Filtrar por email…" style={{ ...inputSt, width: '100%' }} />
                </div>
                {/* Importes */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px', borderRadius: 7, border: '1px solid ' + (filters.include_pricing ? ACCENT+'44' : '#E5E7EB'), background: filters.include_pricing ? LIGHT : '#F9FAFB' }}>
                  <input type="checkbox" checked={filters.include_pricing} onChange={e => ff({ include_pricing: e.target.checked })} style={{ accentColor: ACCENT, width: 14, height: 14 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Incluir importes</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>Requiere tarifas configuradas</div>
                  </div>
                </label>
              </div>

              {/* Botón generar */}
              <Button variant="primary" onClick={runReport} disabled={loading} style={{
                width: '100%', padding: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <MIcon name={loading ? 'hourglass_empty' : 'play_arrow'} size={16} />
                {loading ? 'Generando…' : 'Generar informe'}
              </Button>
            </div>
          </div>

          {/* ── Columna derecha: resultados ── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!report && !loading && (
              <div style={{ background: '#fff', borderRadius: 10, padding: '100px 0', textAlign: 'center' }}>
                <MIcon name="bar_chart" size={52} style={{ display: 'block', margin: '0 auto 12px', color: '#E5E7EB' }} />
                <div style={{ fontSize: 15, fontWeight: 600, color: '#9CA3AF' }}>Configura los filtros y genera el informe</div>
                <div style={{ fontSize: 13, color: '#D1D5DB', marginTop: 4 }}>Los resultados aparecerán aquí</div>
              </div>
            )}
            {report && (
              <>
                {/* Acciones */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button variant="secondary" onClick={() => setSaveModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MIcon name="bookmark_add" size={13} />Guardar filtros
                  </Button>
                  <button onClick={exportCSV} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#059669', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MIcon name="download" size={13} />Exportar CSV
                  </button>
                </div>
                {/* Stats */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <StatCard icon="event_available" label="Reservas"      value={report.summary.total_bookings} />
                  <StatCard icon="schedule"         label="Horas totales" value={`${report.summary.total_hours}h`} color="#7C3AED" />
                  <StatCard icon="calendar_today"   label="Días totales"  value={`${report.summary.total_days}d`}  color="#D97706" />
                  {report.summary.total_amount != null && (
                    <StatCard icon="payments" label="Importe total" value={`${report.summary.total_amount.toFixed(2)} €`} color="#059669" />
                  )}
                </div>
                {filters.group_by !== 'none' && <GroupTable filters={filters} report={report} />}
                <DetailTable rows={report.rows} calcMode={filters.calc_mode} includePricing={filters.include_pricing} />
              </>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: TARIFAS ── */}
      {tab === 'tariffs' && <TariffsTab tariffs={tariffs} setTariffs={setTariffs} rooms={rooms} categories={categories} />}

      {/* ── TAB: GUARDADOS ── */}
      {tab === 'saved' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {saved.length === 0 && (
            <div style={{ background: '#fff', borderRadius: 10, padding: '60px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              No hay informes guardados
            </div>
          )}
          {saved.map(s => (
            <div key={s.id} style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{s.name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                  {s.created_at?.slice(0,10)} · {s.created_by_name || 'Admin'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => { setFilters(JSON.parse(s.filters)); setTab('report'); }}>
                  Cargar
                </Button>
                <Button variant="danger" onClick={() => deleteSaved(s.id)}>
                  <MIcon name="delete" size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal guardar */}
      {saveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 360, boxShadow: '0 8px 40px rgba(0,0,0,.2)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 16 }}>Guardar configuración de informe</div>
            <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Nombre del informe…" autoFocus
              onKeyDown={e => e.key === 'Enter' && doSave()}
              style={{ ...inputSt, width: '100%', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setSaveModal(false)}>Cancelar</Button>
              <Button variant="primary" onClick={doSave}>Guardar</Button>
            </div>
          </div>
        </div>
      )}
    </>
    </BackDeskLayout>
  );
}

// ── Tabla agrupada ──────────────────────────────────────
function GroupTable({ filters, report }) {
  const { group_by, calc_mode, include_pricing } = filters;
  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' };
  const td = { padding: '10px 14px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F9FAFB' };

  let rows = [], cols = [];
  if (group_by === 'category') {
    rows = report.by_category;
    cols = ['Categoría', 'Reservas', calc_mode === 'hours' ? 'Horas' : 'Días', ...(include_pricing ? ['Importe'] : [])];
  } else if (group_by === 'room') {
    rows = report.by_room;
    cols = ['Sala', 'Categoría', 'Ubicación', 'Reservas', calc_mode === 'hours' ? 'Horas' : 'Días', ...(include_pricing ? ['Importe'] : [])];
  } else if (group_by === 'requester') {
    const byReq = {};
    report.rows.forEach(r => {
      const k = r.requester_email || r.requester_name;
      if (!byReq[k]) byReq[k] = { name: r.requester_name, email: r.requester_email, count: 0, hours: 0, days: 0, amount: 0 };
      byReq[k].count++; byReq[k].hours += r.duration_hours; byReq[k].days += r.duration_days;
      if (r.amount) byReq[k].amount += r.amount;
    });
    rows = Object.values(byReq).sort((a,b) => b.count - a.count);
    cols = ['Solicitante', 'Email', 'Reservas', calc_mode === 'hours' ? 'Horas' : 'Días', ...(include_pricing ? ['Importe'] : [])];
  }

  const fmt = (row, isAmount) => {
    if (isAmount) return row.amount > 0 ? `${Math.round(row.amount*100)/100} €` : '—';
    return calc_mode === 'hours' ? `${Math.round(row.hours*100)/100}h` : `${Math.round(row.days*100)/100}d`;
  };

  return (
    <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', fontSize: 13, fontWeight: 700, color: NAVY }}>
        <MIcon name="table_chart" size={14} style={{ marginRight: 6, color: '#9CA3AF' }} />
        Resumen por {group_by === 'category' ? 'categoría' : group_by === 'room' ? 'sala' : 'solicitante'}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{cols.map(c => <th key={c} style={th}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ background: i%2===0 ? '#fff' : '#F9FAFB' }}>
                {group_by === 'category' && <>
                  <td style={td}><span style={{ width: 8, height: 8, borderRadius: '50%', background: row.color, display: 'inline-block', marginRight: 8 }} /><b>{row.name}</b></td>
                  <td style={{ ...td, fontWeight: 700, color: '#0085CA' }}>{row.count}</td>
                  <td style={td}>{fmt(row)}</td>
                  {include_pricing && <td style={{ ...td, color: '#059669', fontWeight: 600 }}>{fmt(row, true)}</td>}
                </>}
                {group_by === 'room' && <>
                  <td style={{ ...td, fontWeight: 600 }}>{row.name}</td>
                  <td style={td}><span style={{ fontSize: 11, background: '#F3F4F6', padding: '2px 7px', borderRadius: 8 }}>{row.category}</span></td>
                  <td style={{ ...td, color: '#9CA3AF' }}>{row.location || '—'}</td>
                  <td style={{ ...td, fontWeight: 700, color: '#0085CA' }}>{row.count}</td>
                  <td style={td}>{fmt(row)}</td>
                  {include_pricing && <td style={{ ...td, color: '#059669', fontWeight: 600 }}>{fmt(row, true)}</td>}
                </>}
                {group_by === 'requester' && <>
                  <td style={{ ...td, fontWeight: 600 }}>{row.name}</td>
                  <td style={{ ...td, color: '#9CA3AF' }}>{row.email}</td>
                  <td style={{ ...td, fontWeight: 700, color: '#0085CA' }}>{row.count}</td>
                  <td style={td}>{fmt(row)}</td>
                  {include_pricing && <td style={{ ...td, color: '#059669', fontWeight: 600 }}>{fmt(row, true)}</td>}
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tabla detallada ─────────────────────────────────────
function DetailTable({ rows, calcMode, includePricing }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 25);
  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' };
  const td = { padding: '9px 14px', fontSize: 12, color: '#374151', borderBottom: '1px solid #F9FAFB' };

  return (
    <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
          <MIcon name="list_alt" size={14} style={{ marginRight: 6, color: '#9CA3AF' }} />
          Detalle de reservas <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({rows.length} resultados)</span>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Título','Solicitante','Sala','Categoría','Inicio','Fin', calcMode==='hours'?'Horas':'Días','Estado', ...(includePricing?['Importe']:[])].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={r.id} style={{ background: i%2===0 ? '#fff' : '#F9FAFB' }}>
                <td style={{ ...td, fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</td>
                <td style={td}>
                  <div style={{ fontWeight: 500 }}>{r.requester_name}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{r.requester_email}</div>
                </td>
                <td style={{ ...td, fontWeight: 500 }}>{r.room_name}</td>
                <td style={td}><span style={{ background: (r.category_color||'#ccc')+'22', color: r.category_color||'#666', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{r.category_name}</span></td>
                <td style={{ ...td, color: '#6B7280' }}>{r.start_time?.slice(0,16).replace('T',' ')}</td>
                <td style={{ ...td, color: '#6B7280' }}>{r.end_time?.slice(0,16).replace('T',' ')}</td>
                <td style={{ ...td, fontWeight: 700, color: NAVY }}>{calcMode==='hours' ? `${r.duration_hours}h` : `${r.duration_days}d`}</td>
                <td style={td}><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: r.status==='confirmed'?'#D1FAE5':'#FEF3C7', color: r.status==='confirmed'?'#065F46':'#92400E' }}>{r.status==='confirmed'?'Confirmada':'Pendiente'}</span></td>
                {includePricing && <td style={{ ...td, color: '#059669', fontWeight: 600 }}>{r.amount!=null?`${r.amount} ${r.currency}`:'—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 25 && (
        <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid #F3F4F6' }}>
          <Button variant="secondary" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Ver menos' : `Ver ${rows.length-25} más`}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Tarifas ─────────────────────────────────────────────
function TariffsTab({ tariffs, setTariffs, rooms, categories }) {
  const [form,    setForm]    = useState({ name:'', room_id:'', category_id:'', price_hour:'', price_day:'', currency:'EUR', notes:'' });
  const [editing, setEditing] = useState(null);
  const ff = v => setForm(p => ({ ...p, ...v }));
  const st = { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid #F3F4F6' };
  const td = { padding: '10px 14px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F9FAFB' };

  async function save() {
    const method = editing ? 'PUT' : 'POST';
    const url    = editing ? `/api/reports/tariffs/${editing}` : '/api/reports/tariffs';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hd_token')}` },
      body: JSON.stringify({ ...form, room_id: form.room_id||null, category_id: form.category_id||null, price_hour: form.price_hour?Number(form.price_hour):null, price_day: form.price_day?Number(form.price_day):null }),
    });
    const t = await res.json();
    setTariffs(p => editing ? p.map(x => x.id===editing ? t : x) : [t, ...p]);
    setForm({ name:'', room_id:'', category_id:'', price_hour:'', price_day:'', currency:'EUR', notes:'' });
    setEditing(null);
  }

  async function del(id) {
    await fetch(`/api/reports/tariffs/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${localStorage.getItem('hd_token')}` } });
    setTariffs(p => p.filter(t => t.id!==id));
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, alignItems: 'start' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{editing ? 'Editar tarifa' : 'Nueva tarifa'}</div>
        <div style={{ fontSize: 12, color: '#9CA3AF' }}>Las tarifas son opcionales. Sin tarifas, los informes solo muestran datos de uso.</div>
        {[['Nombre','name','text','Ej. Pistas deportivas'],['€/hora (opcional)','price_hour','number','0.00'],['€/día (opcional)','price_day','number','0.00']].map(([label,key,type,placeholder]) => (
          <div key={key}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>{label}</div>
            <input type={type} value={form[key]} onChange={e => ff({ [key]: e.target.value })} placeholder={placeholder} style={st} />
          </div>
        ))}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>Sala específica</div>
          <select value={form.room_id} onChange={e => ff({ room_id: e.target.value, category_id: '' })} style={{ ...st, cursor: 'pointer' }}>
            <option value="">— Por categoría —</option>
            {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>Categoría</div>
          <select value={form.category_id} onChange={e => ff({ category_id: e.target.value, room_id: '' })} style={{ ...st, cursor: 'pointer' }} disabled={!!form.room_id}>
            <option value="">— Seleccionar —</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>Moneda</div>
          <select value={form.currency} onChange={e => ff({ currency: e.target.value })} style={{ ...st, cursor: 'pointer' }}>
            {['EUR','USD','GBP','COP','DOP','AOA'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          {editing && <Button variant="secondary" onClick={() => { setEditing(null); setForm({ name:'', room_id:'', category_id:'', price_hour:'', price_day:'', currency:'EUR', notes:'' }); }} style={{ flex:1 }}>Cancelar</Button>}
          <Button variant="primary" onClick={save} style={{ flex:1 }}>{editing?'Guardar cambios':'Añadir tarifa'}</Button>
        </div>
      </div>
      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', fontSize: 13, fontWeight: 700, color: NAVY }}>
          Tarifas configuradas <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({tariffs.length})</span>
        </div>
        {tariffs.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
            <MIcon name="payments" size={36} style={{ display: 'block', margin: '0 auto 8px', color: '#E5E7EB' }} />
            Sin tarifas — los informes se generan sin importes
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Nombre','Aplica a','€/hora','€/día','Moneda',''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {tariffs.map((t, i) => (
                <tr key={t.id} style={{ background: i%2===0?'#fff':'#F9FAFB' }}>
                  <td style={{ ...td, fontWeight: 600 }}>{t.name}</td>
                  <td style={td}>{t.room_name ? `Sala: ${t.room_name}` : t.category_name ? `Cat: ${t.category_name}` : 'Global'}</td>
                  <td style={td}>{t.price_hour!=null ? `${t.price_hour} ${t.currency}` : '—'}</td>
                  <td style={td}>{t.price_day!=null  ? `${t.price_day} ${t.currency}`  : '—'}</td>
                  <td style={td}>{t.currency}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="secondary" size="sm" onClick={() => { setEditing(t.id); setForm({ name:t.name, room_id:t.room_id||'', category_id:t.category_id||'', price_hour:t.price_hour??'', price_day:t.price_day??'', currency:t.currency, notes:t.notes||'' }); }}>Editar</Button>
                      <Button variant="danger" size="sm" onClick={() => del(t.id)}>
                        <MIcon name="delete" size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
